import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Instagram, Facebook, Mail, Phone, MapPin, Menu, X } from "lucide-react";
import { LOGO_URL } from "./brand";
import api from "../lib/api";

const navItems = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/courses", label: "Course" },
  { to: "/schedule", label: "Schedule" },
  { to: "/gallery", label: "Gallery" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

function buildSocialUrl(platform, handle) {
  if (!handle) return platform === "instagram" ? "https://instagram.com" : "https://facebook.com";
  const h = String(handle).trim().replace(/^@/, "");
  // Already a full URL?
  if (/^https?:\/\//i.test(h)) return h;
  return platform === "instagram" ? `https://instagram.com/${h}` : `https://facebook.com/${h}`;
}

export default function PublicLayout() {
  const [settings, setSettings] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    api.get("/public/settings").then((r) => setSettings(r.data)).catch((e) => console.warn("[layout] settings fetch failed:", e));
  }, []);
  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  const igUrl = buildSocialUrl("instagram", settings?.instagram_handle);
  const fbUrl = buildSocialUrl("facebook", settings?.facebook_handle);
  const phone = settings?.contact_phone || "020 3475 9229";
  const email = settings?.contact_email || "seasonsartclasscroydon@gmail.com";
  const addr = settings?.contact_address || "East Croydon, London";
  return (
    <div className="min-h-screen flex flex-col" data-testid="public-layout">
      {/* Webmaster verification tags injected from admin Settings → SEO */}
      <Helmet>
        {settings?.google_site_verification && (
          <meta name="google-site-verification" content={settings.google_site_verification} />
        )}
        {settings?.bing_site_verification && (
          <meta name="msvalidate.01" content={settings.bing_site_verification} />
        )}
      </Helmet>
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#FAF6EE]/85 border-b border-[#E8E2D5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-home-link">
            <img src={LOGO_URL} alt="The Seasons Art Class — East Croydon" className="h-12 w-auto object-contain" />
          </Link>
          <nav className="mx-auto hidden lg:flex items-center gap-5 xl:gap-6">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-body text-[15px] transition-colors whitespace-nowrap ${
                    isActive ? "text-[#5D2C8F] font-medium" : "text-[#8A6DA3] hover:text-[#5D2C8F]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <Link to="/contact" className="sac-btn-primary shrink-0 ml-auto lg:ml-0" data-testid="header-enquire-btn">
            Enquire
          </Link>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden ml-2 p-2 -mr-1 text-[#5D2C8F] rounded-lg hover:bg-[#EADCF0]"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile / tablet menu panel */}
        {menuOpen && (
          <div className="lg:hidden border-t border-[#E8E2D5] bg-[#FAF6EE]/98 backdrop-blur-md" data-testid="mobile-menu-panel">
            <nav className="max-w-7xl mx-auto px-6 py-3 flex flex-col">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `py-3 px-2 font-body text-base border-b border-[#F0E8D5] last:border-b-0 transition-colors ${
                      isActive ? "text-[#5D2C8F] font-medium" : "text-[#6E5A80] hover:text-[#5D2C8F]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="mt-24 bg-[#3A2A47] text-[#F4EDDD]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-10">
          <div>
            <img src={LOGO_URL} alt="Seasons Art Class East Croydon" className="h-24 w-auto object-contain mb-4 bg-white/5 rounded-xl p-2" />
            <p className="text-sm text-[#F4EDDD]/80 leading-relaxed">
              The Art Class You Always Wanted. 14-week step-by-step courses for adults in East Croydon.
            </p>
          </div>
          <div>
            <div className="font-display text-xl mb-3">Explore</div>
            <ul className="space-y-2 text-sm text-[#F4EDDD]/80">
              {navItems.slice(1).map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="hover:text-[#E8C37A]">{n.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="font-display text-xl mb-3">Say hello</div>
            <ul className="space-y-2 text-sm text-[#F4EDDD]/80">
              <li className="flex items-center gap-2"><Phone size={16} /> {phone}</li>
              <li className="flex items-center gap-2"><Mail size={16} /> {email}</li>
              <li className="flex items-center gap-2"><MapPin size={16} /> {addr}</li>
            </ul>
          </div>
          <div>
            <div className="font-display text-xl mb-3">Follow along</div>
            <div className="flex items-center gap-3">
              <a href={igUrl} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/10 hover:bg-[#E8C37A]" data-testid="footer-instagram"><Instagram size={18} /></a>
              <a href={fbUrl} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/10 hover:bg-[#E8C37A]" data-testid="footer-facebook"><Facebook size={18} /></a>
            </div>
            <Link to="/admin/login" className="text-xs text-[#F4EDDD]/50 hover:text-[#E8C37A] mt-6 inline-block" data-testid="footer-admin-link">Admin access</Link>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-[#F4EDDD]/50">
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mb-2">
            <Link to="/terms" className="hover:text-[#E8C37A]" data-testid="footer-terms">Terms & Conditions</Link>
            <Link to="/privacy" className="hover:text-[#E8C37A]" data-testid="footer-privacy">Privacy</Link>
            <Link to="/cookies" className="hover:text-[#E8C37A]" data-testid="footer-cookies">Cookies</Link>
            <Link to="/accessibility" className="hover:text-[#E8C37A]" data-testid="footer-accessibility">Accessibility</Link>
          </div>
          © {new Date().getFullYear()} The Seasons Art Class — East Croydon. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
